'use client';

import { ReactNode, useMemo } from 'react';

import { AvatarSize, sizeToContainerSize } from '.';

type Gradient = {
  startColor?: string;
  endColor?: string;
};

export const ProgressCircle: React.FC<{
  percentage?: number;
  borderColor: string;
  innerPadding?: number;
  height?: number;
  width?: number;
  borderWidth?: number;
  backgroundColor?: string;
  children?: ReactNode;
  gradient?: Gradient;
}> = ({
  percentage = 100,
  height = 32,
  width = 32,
  borderWidth = 2,
  innerPadding = 3,
  backgroundColor = 'transparent',
  borderColor = 'green',
  children,
  gradient,
}) => {
  const radius = Math.min(height, width) / 2 - borderWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference - (percentage / 100) * circumference;

  const offsetHeight = height - borderWidth * 2 - innerPadding;
  const offsetWidth = width - borderWidth * 2 - innerPadding;

  const gradientId = useMemo(
    () => `backgroundColor-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  return (
    <div
      className={`relative z-10 flex h-[${height}px] w-[${width}px] items-center justify-center p-0`}
    >
      <svg className='absolute' height={height} width={width}>
        {gradient && (
          <defs>
            <linearGradient
              id={gradientId}
              x1='100%'
              y1='0%'
              x2='0%'
              y2='0%'
              filter='url(#filter0_d)'
            >
              <stop
                offset='25%'
                stopColor={gradient.startColor || 'transparent'}
                stopOpacity={0.7}
              />
              <stop
                offset='200%'
                stopColor={gradient.endColor || 'transparent'}
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
        )}
        <circle
          cx={radius + borderWidth / 2}
          cy={radius + borderWidth / 2}
          r={radius}
          stroke={borderColor}
          strokeWidth={borderWidth}
          strokeOpacity={gradient ? 0.1 : 1}
          fill={gradient ? `url(#${gradientId})` : backgroundColor}
          strokeDasharray={circumference}
          strokeDashoffset={strokeOffset}
          style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
        />
      </svg>
      <div
        className={`z-10 flex items-center justify-center h-[${offsetHeight}px] w-[${offsetWidth}px]`}
      >
        {children}
      </div>
    </div>
  );
};

export const ProgressContainer: React.FC<{
  children: ReactNode;
  percentage: number;
  innerPadding?: number;
  borderWidth?: number;
  size?: AvatarSize;
}> = ({ children, percentage, innerPadding = 2, borderWidth = 2, size = 'md' }) => {
  const containerSize = sizeToContainerSize[size];
  const circleHeight = containerSize + innerPadding + borderWidth + 2;
  const circleWidth = containerSize + innerPadding + borderWidth + 2;
  const borderColor = percentage === 100 ? '#6EE7B7' : '#FBBF24'; // emerald-300, amber-400

  return (
    <div className='flex'>
      <ProgressCircle
        width={circleWidth}
        height={circleHeight}
        percentage={percentage}
        borderColor={borderColor}
        borderWidth={borderWidth}
        backgroundColor='white'
      >
        {children}
      </ProgressCircle>
    </div>
  );
};
